<template>
    <simple-layout>
        <basic-card>
            <template #header>
                {{ $t('market.volatilities') }}
            </template>

            <data-table
              :data="volatilities"
              :configuration="volatilitiesTableConfig"
              @focusedChanged="handleVolatilityFocus"
              component-id="volatility-rate-table"
            >
                <template v-if="focusedVolatility">
                    <basic-card>
                        <template #header>Detail - {{ `${focusedVolatility.name}` }}</template>


                        <toolbox>
                            <toolbox-button :help-text="`${$t('searchQuery.exportExcel')}`"
                                            @press="$modal.show('exportIntervalModal')">
                                <icon :icon="['fal', 'file-excel']" size="lg"/>
                            </toolbox-button>
                        </toolbox>

                        <form-wrapper :form-title="$t('detail')">
                            <form-text-input v-model="focusedVolatility.name" :caption="`${$t('name')}`"/>
                            <form-select-input :caption="`${$t('exchangeRate')}`"
                                               :options="exchangeRatesOptions"
                                               v-model="focusedVolatility.exchangeRateId"/>
                        </form-wrapper>

                        <framed-content :title="$t('gridPoints')">
                            <data-table
                              :data="focusedVolatility.gridPoints"
                              :configuration="gridPointsTableConfig"
                              @add="gridPointAdd"
                              @delete="gridPointDelete"
                              @focusedChanged="handleGridPointFocus"
                              component-id="operation-definition-parameters-list"
                            >
                                <template v-if="focusedGridPoint">
                                    <basic-card>
                                        <form-wrapper>
                                            <form-text-input v-model="focusedGridPoint.name"
                                                             :caption="`${$t('name')}`"/>
                                            <form-text-input v-model="focusedGridPoint.term" :caption="$t('term')"/>
                                            <simple-button @press="handleSaveGridPoint">
                                                {{ $t('save') }}
                                            </simple-button>
                                        </form-wrapper>
                                    </basic-card>
                                </template>
                            </data-table>
                        </framed-content>

                        <!--2. level-->
                        <framed-content :title="$t('curves') ">

                            <data-table
                              :data="curves"
                              :configuration="curvesTableConfig"
                              @focusedChanged="handleCurveFocus"
                              component-id="operation-definition-parameters-list"
                            >
                                <template v-if="focusedCurve">
                                    <basic-card>
                                        <template #header>
                                            {{
                                                `${focusedVolatility.name} - ${$moment(focusedCurve.validDate).format('DD.MM.YYYY')}`
                                            }}
                                        </template>
                                        <data-table
                                          :data="curvesValues"
                                          :configuration="curvesValuesTableConfig"
                                          @add="curveValueAdd"
                                          @delete="curveValueDelete"
                                          @focusedChanged="handleCurveValueFocus"
                                        >
                                            <template v-if="focusedCurveValue">
                                                <basic-card>
                                                    <form-wrapper>
                                                        <form-text-input v-model="focusedCurveValue.termName"
                                                                         :caption="`${$t('name')}`"/>
                                                        <form-text-input v-model="focusedCurveValue.value"
                                                                         :caption="$t('term')"/>
                                                        <simple-button @press="handleSaveCurveValue">
                                                            {{ $t('save') }}
                                                        </simple-button>
                                                    </form-wrapper>
                                                </basic-card>
                                            </template>
                                        </data-table>
                                    </basic-card>
                                </template>


                            </data-table>

                        </framed-content>


                        <simple-button confirmation-protected
                                       :protection-message="`${$t('savingVol')} ${focusedVolatility.name}`"
                                       @proceed="handleSave">
                            {{ `${$t('save')} ${focusedVolatility.name}` }}
                        </simple-button>

                    </basic-card>
                </template>
            </data-table>


        </basic-card>

        <v-modal name="exportIntervalModal"
                 classes="transparent-modal"
                 style=" background-color: rgba(10,10,10,0.66)">
            <basic-card style="min-height: 400px; box-shadow: none !important;">
                <form-wrapper :form-title="$t('exportInterval')">
                    <form-date-input :caption="$t('dateFrom')" v-model="exportDateFrom"/>
                    <form-date-input :caption="$t('dateThru')" v-model="exportDateThru"/>
                </form-wrapper>

                <simple-button :disabled="!exportDateFrom || !exportDateThru" @press="handleExport">{{
                        $t('startExport')
                    }}
                </simple-button>


            </basic-card>
        </v-modal>

    </simple-layout>
</template>

<script>
import SimpleLayout from "@/components/layout/SimpleLayout";
import BasicCard from "@/components/layout/BasicCard";
import DataTable from "@/components/table/DataTable";
import Axios from "axios";
import { mapGetters } from "vuex";
import FormTextInput from "@/components/form/FormTextInput";
import FormWrapper from "@/components/form/FormWrapper";
import FormSelectInput from "@/components/form/FormSelectInput";
import CollapsibleContent from "@/components/CollapsibleContent";
import SimpleButton from "@/components/button/SimpleButton";
import FramedContent from "@/components/layout/FramedContent";
import handleTableExport from "@/utils/excel-utils";
import ToolboxButton from "@/components/toolbox/ToolboxButton";
import Toolbox from "@/components/toolbox/Toolbox";
import FormDateInput from "@/components/form/FormDateInput";
import { isInInterval } from "@/utils/date-utils";


export default {
    name: "VolatilitiesView",
    components: {
        FormDateInput,
        Toolbox,
        ToolboxButton,
        FramedContent,
        SimpleButton,
        CollapsibleContent, FormSelectInput, FormWrapper, FormTextInput, DataTable, BasicCard, SimpleLayout
    },
    data: function () {
        return {
            exportDateFrom: null,
            exportDateThru: null,

            focusedVolatility: {},
            focusedGridPoint: null,
            focusedCurve: {},
            focusedCurveValue: null,
            volatilities: [],
            curves: [],

            volatilitiesTableConfig: {
                columns: [
                    {
                        caption: this.$t('name'),
                        datum: d => d.name
                    },
                    {
                        caption: this.$t('market.rate'),
                        datum: d => d.exchangeRateCode
                    },

                ],
                allowEdit: true,
            },

            gridPointsTableConfig: {
                columns: [

                    {
                        caption: this.$t('name'),
                        datum: d => d.name
                    },
                    {
                        caption: this.$t('term'),
                        datum: d => d.term
                    },
                ],
                enableAdd: true,
                allowEdit: true,
                // actions: [
                //     // {
                //     //     name: "delete",
                //     //     icon: "trash",
                //     //     tooltip: this.$t('operationDefinition.delete')
                //     // },
                // ]
            },

            curvesTableConfig: {
                columns: [
                    {
                        caption: this.$t('market.validDate'),
                        datum: d => this.$moment(d.validDate).format('DD.MM.YYYY')
                    },
                ],
                allowEdit: true,
            },

            curvesValuesTableConfig: {
                columns: [

                    {
                        caption: this.$t('gridPoint'),
                        datum: d => d.termName
                    },

                    {
                        caption: this.$t('market.value'),
                        datum: d => d.value
                    },
                ],
                enableAdd: true,
                allowEdit: true,
            },

        }
    },
    computed: {
        ...mapGetters({
            exchangeRates: "exchangeRates/exchangeRates",
            exchangeRatesOptions: "currencies/exchangeRatesOptions"
        }),

        curvesValues() {
            return this.focusedCurve?.values?.map((value, index) => ({
                termName: this.focusedVolatility?.gridPoints[index]?.name ?? 'N/A',
                value: value / 100000
            })) ?? [];
        },
    },
    methods: {

        async handleVolatilityFocus(volatility) {
            this.focusedVolatility = volatility;

            if (volatility) {
                const response = await Axios.get(`currency/api/v1/volatility/${volatility.id}/curves?size=1000&sortField=validDate&sortDirection=-1`);
                this.curves = response.data;
            } else {
                this.curves = [];
            }
        },

        handleCurveFocus(curve) {
            this.focusedCurve = curve;
        },

        handleGridPointFocus(gridPoint) {
            this.focusedGridPoint = gridPoint;
        },

        gridPointAdd() {
            this.focusedGridPoint = {
                name: null,
                term: null
            }
        },
        gridPointDelete(gridPoint) {
            const index = this.focusedVolatility.gridPoints.findIndex(gp => gp === gridPoint);
            this.focusedVolatility.gridPoints.splice(index, 1);
        },
        handleSaveGridPoint() {
            const gridPoint = this.focusedVolatility.gridPoints.find(gp => gp.name === this.focusedGridPoint.name);
            if (gridPoint) {
                gridPoint.term = this.focusedGridPoint.term;
            } else {
                this.focusedVolatility.gridPoints.push(this.focusedGridPoint);
                this.focusedVolatility.gridPoints.sort((a, b) => a.term - b.term)
            }
            this.focusedGridPoint = null;
        },
        curveValueAdd() {
            this.focusedCurveValue = {
                termName: null,
                value: null
            }
        },
        curveValueDelete(curveValue) {
            const index = this.curvesValues.findIndex(gp => gp === curveValue);
            this.curvesValues.splice(index, 1);
        },
        handleCurveValueFocus(curveValue) {
            this.focusedCurveValue = curveValue;
        },

        async handleSaveCurveValue() {
            const curveValue = this.curvesValues.find(cv => cv.termName === this.focusedCurveValue.termName);
            if (curveValue) {
                curveValue.value = this.focusedCurveValue.value;
            } else {
                this.curvesValues.push(this.focusedCurveValue);
                // this.curvesValues.sort((a, b) => a.termName - b.termName)
            }
            this.focusedCurveValue = null;

            const curveToSave = this.$_.cloneDeep(this.focusedCurve);
            curveToSave.values = this.curvesValues.map(cv => cv.value * 100000);

            const response = await Axios.post(`currency/api/v1/volatility/${this.focusedVolatility.id}/curves`, curveToSave);


        },

        async handleSave() {
            try {
                const response = await Axios.put(`currency/api/v1/volatility/${this.focusedVolatility.id}`, this.focusedVolatility);
                this.$notify({
                    group: 'application',
                    title: this.$t('volatilities.saveSuccess'),
                    text: this.$t('volatilities.saveSuccess'),
                    type: "ok"
                })


            } catch (e) {
                this.$notify({
                    group: 'application',
                    title: this.$t('volatilities.saveError'),
                    text: this.$t('volatilities.saveError'),
                    type: "error"
                })
            }
        },

        async handleExport() {

            try {
                const filteredCurves = this.$_.cloneDeep(this.curves).filter(isInInterval('validDate', this.exportDateFrom, this.exportDateThru));

                const exportedData = filteredCurves.flatMap(curve => {
                    return curve.values.map((value, index) => ({
                        validDate: curve.validDate,
                        name: this.focusedVolatility.name,
                        termName: this.focusedVolatility?.gridPoints[index]?.name ?? 'N/A',
                        term: this.focusedVolatility?.gridPoints[index]?.term ?? 'N/A',
                        value: value / 100000
                    })) ?? [];
                });


                // console.dir(exportedData)

                const columns = [
                    this.curvesTableConfig.columns[0], //this.$moment(d.validDate).format('DD.MM.YYYY')
                    this.volatilitiesTableConfig.columns[0], // d.name
                    this.curvesValuesTableConfig.columns[0], // d.termName
                    this.gridPointsTableConfig.columns[1], // d.term
                    this.curvesValuesTableConfig.columns[1], // d.value

                ]

                await handleTableExport(columns, exportedData, `volatility_${this.focusedVolatility.name}`);

                this.$modal.hide('exportIntervalModal');
                this.$notify({
                    group: 'application',
                    title: this.$t('exportSuccess'),
                    text: this.$t('exportSuccess'),
                    type: "ok"
                })
            } catch (error) {
                this.$notify({
                    group: 'application',
                    title: this.$t('exportError'),
                    text: this.$t('exportError'),
                    type: "error"
                })
            }
        },
    },
    async mounted() {
        await this.$store.dispatch("exchangeRates/refreshExchangeRates")

        const response = await Axios.get('currency/api/v1/volatility?size=10000');

        for (const value of response.data) {
            value.exchangeRateCode = this.exchangeRates.find(rate => rate.id === value.exchangeRateId)?.systemName
        }

        this.volatilities = response.data;
    }
}
</script>

<style scoped>

</style>
